import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_voyage-squad-3/artifacts/242nersf_final%20llogo.png";

export default function Privacy() {
  return (
    <div data-testid="privacy-page" className="min-h-screen bg-[#FDFBF7] text-[#1A1A1A]">
      <header className="sticky top-0 z-30 bg-[#FDFBF7]/85 backdrop-blur-md border-b border-[#E5E0D8]/60">
        <div className="max-w-3xl mx-auto px-5 md:px-8 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="privacy-logo">
            <img src={LOGO_URL} alt="Fourdays" className="h-12 md:h-16 w-auto object-contain" />
          </Link>
          <Link
            to="/"
            data-testid="privacy-back"
            className="inline-flex items-center gap-1.5 rounded-full bg-[#1A1A1A] hover:bg-black text-white text-sm font-semibold px-4 py-2 transition active:scale-95"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-8 py-10 md:py-16">
        <h1 className="font-display font-extrabold text-3xl md:text-5xl tracking-tight">
          Privacy Policy
        </h1>
        <p className="text-sm text-[#5C5C5C] mt-2">Last updated: 16 June 2026</p>

        <div className="prose prose-stone max-w-none mt-8 text-[15px] md:text-base leading-relaxed text-[#1A1A1A] space-y-5">
          <p>
            This Privacy Policy explains how <strong>Fourdays</strong> ("we", "us", "our") collects,
            uses, and protects your personal information when you use our website, sign-up forms,
            and WhatsApp-based matching service (the "Service").
          </p>
          <p>
            By using the Service, you agree to the practices described here. If you do not agree,
            please do not use the Service.
          </p>
          <p>
            Fourdays is currently operating a limited beta in Goa, India. This policy reflects that
            beta stage and may be updated as the Service evolves.
          </p>

          <Section title="1. Who we are">
            <p>
              Fourdays is an early-stage travel-matching service that helps travellers form small
              groups (typically 4–5 people) for shared activities and routes within a specific local
              area. During the beta, matching is coordinated manually and groups are organised over
              WhatsApp.
            </p>
          </Section>

          <Section title="2. Information we collect">
            <p>When you sign up or interact with the Service, we may collect:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#FF5A36]">
              <li><strong>Contact details</strong> — your name (or first name) and phone number.</li>
              <li>
                <strong>Profile details you choose to provide</strong> — such as age range, gender,
                travel dates, location/corridor, activity preferences, and "vibe" tags.
              </li>
              <li><strong>A profile photo</strong>, if you choose to provide one.</li>
              <li>
                <strong>Activity and route requests</strong> — the plans, times, and places you submit
                to be matched around.
              </li>
              <li>
                <strong>Communications</strong> — messages you send us or share within WhatsApp groups
                we create.
              </li>
              <li>
                <strong>Basic technical data</strong> — such as device/browser information and general
                analytics when you visit the website.
              </li>
            </ul>
            <p>We only collect what we need to operate the matching service during the beta.</p>
          </Section>

          <Section title="3. How we use your information">
            <p>We use your information to:</p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#FF5A36]">
              <li>Match you with other travellers for activities and routes.</li>
              <li>Create and manage WhatsApp groups for matched travellers.</li>
              <li>Communicate with you about your requests, matches, and the beta.</li>
              <li>
                Apply our trust and safety measures (such as small-group limits and, where offered,
                women-only groups).
              </li>
              <li>Improve the Service and understand how the beta is performing.</li>
            </ul>
            <p>We do <strong>not</strong> sell your personal information.</p>
          </Section>

          <Section title="4. How we share your information">
            <ul className="list-disc pl-5 space-y-3 marker:text-[#FF5A36]">
              <li>
                <strong>Via WhatsApp groups, once you are matched.</strong> We do not display your
                profile to other travellers on our website or platform, and we do not share your
                personal details with other travellers ourselves. Your details only become visible to
                others when you are added to a WhatsApp group with your matched travellers. At that
                point, other group members can see whatever WhatsApp shows (such as your phone number
                and WhatsApp display name/photo), along with anything you choose to share about
                yourself in the group. Whatever you tell the group about yourself is shared by you,
                at your discretion — please only share what you are comfortable with others seeing.
                Your use of WhatsApp is also governed by WhatsApp's own privacy policy.
              </li>
              <li>
                <strong>With service providers</strong> who help us run the Service (such as form,
                hosting, or analytics tools), only as needed.
              </li>
              <li>
                <strong>Where required by law</strong>, or to protect the rights, safety, or property
                of users or others.
              </li>
            </ul>
          </Section>

          <Section title="5. Consent to WhatsApp groups">
            <p>
              By submitting your details, you consent to being contacted on WhatsApp and to being
              added to WhatsApp group(s) with other matched travellers. You can leave any group at
              any time, and you can ask us to remove your details (see "Your rights" below).
            </p>
          </Section>

          <Section title="6. Data storage and security">
            <p>
              During the beta, your information is stored in coordination tools and a structured
              database used to organise matches. We take reasonable steps to protect your
              information, but no method of transmission or storage is completely secure, and we
              cannot guarantee absolute security.
            </p>
            <p>
              We keep your information only as long as needed for the beta and the purposes described
              here, after which we will delete or anonymise it.
            </p>
          </Section>

          <Section title="7. Your rights">
            <p>
              Depending on where you live (including under India's Digital Personal Data Protection
              Act and, for EU/UK travellers, the GDPR), you may have the right to:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-[#FF5A36]">
              <li>Access the personal data we hold about you.</li>
              <li>Correct inaccurate data.</li>
              <li>Request deletion of your data.</li>
              <li>Withdraw consent at any time.</li>
              <li>Object to or restrict certain processing.</li>
            </ul>
            <p>To exercise any of these, send us a message and we will respond within a reasonable time.</p>
          </Section>

          <Section title="8. International travellers">
            <p>
              Fourdays operates in India. If you are visiting from another country, your information
              will be processed in India. By using the Service, you understand and consent to your
              information being processed here.
            </p>
          </Section>

          <Section title="9. Age requirement">
            <p>
              The Service is only for people <strong>aged 18 and over</strong>. We do not knowingly
              collect information from anyone under 18. If you believe someone under 18 has provided
              us information, contact us and we will delete it.
            </p>
          </Section>

          <Section title="10. Changes to this policy">
            <p>
              We may update this Privacy Policy as the Service develops. We will post the updated
              version with a new "Last updated" date. Continued use of the Service after changes
              means you accept the updated policy.
            </p>
          </Section>

          <p className="text-sm text-[#5C5C5C] italic pt-6 border-t border-[#E5E0D8]">
            This document is a general template provided for an early-stage beta and does not
            constitute legal advice. You should have it reviewed by a qualified lawyer, particularly
            before scaling beyond the beta.
          </p>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="pt-2">
      <h2 className="font-display font-extrabold text-xl md:text-2xl tracking-tight mt-6 mb-3">
        {title}
      </h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
